import { createContext, useContext, useMemo, useCallback } from 'react';
import type { ClassificationSchema, ClassificationVocabulary, FlagLanding } from '../services/api';
import { resolveLabel, resolveColor, resolvePrimarySwatch, isUnclassifiedValue, resolveAbbr, resolveIcon, resolveHint, resolveAttribute, resolveAttributeFace, resolveFace, resolveServedWord, resolveCategoryNoun, resolveSecondaryAxisNoun, resolveSecondaryAxisNounPlural, resolveCategoriesForSecondaryAxisCode } from '../services/categoryMeta';
import { ownsMsbKVocabulary, isKnownRole, ROW_LAYOUT_DEFAULT_BY_ROLE, type KnownRole } from '../services/roleRegistry';
import { CATEGORY_KEYS, UNCLASSIFIED_KEY, formatSecondaryAxisCode, getReasonCodeLabel } from '../constants';

// gMSB default: all K codes except KU (mirrors the stats views' existing CATEGORY_KEYS.filter)
const GMSB_STAT_KEYS = CATEGORY_KEYS.filter(k => k !== UNCLASSIFIED_KEY) as string[];

export type RowLayout = 'strip_label' | 'strip_abbr';

// Role-aware default for the ticket-row category display, shared by App (effective default)
// and EinstellungenSeite (the toggle's shown default + changes-check) so they can't drift.
// The per-role answer itself is declared exhaustively in ROW_LAYOUT_DEFAULT_BY_ROLE
// (services/roleRegistry.ts) — this function's only job is the lookup plus the unresolved-role
// floor below. A saved operator override (UI_ROW_LAYOUT) wins over whatever this returns.
// Accepts string|null|undefined (not just KnownRole) because both callers are legitimate: App.tsx
// passes the already-validated KnownRole|null, EinstellungenSeite passes its own `role: string`
// prop verbatim.
export function defaultRowLayout(role: string | null | undefined): RowLayout {
  // An unresolved or unrecognised role gets the MAJORITY posture (three of four known roles), not
  // the single named exception's. vnb is the one role whose long marktprozess labels read better
  // as a row-2 badge; a role this function has never seen is not vnb, so it must not silently land
  // on vnb's posture just because that happened to be the fall-through branch — that exact
  // silent-inheritance shape is how a real role ended up on the wrong default before this map existed.
  return role && isKnownRole(role) ? ROW_LAYOUT_DEFAULT_BY_ROLE[role] : 'strip_abbr';
}

type Ctx = {
  schema: ClassificationSchema | null;
  // The role's classification vocabulary as DATA (BE /config). When present it WINS over every
  // path below it — that is what makes a label rename one schema edit instead of a code change,
  // and what lets a future schema-driven role need no frontend change at all.
  vocabulary?: ClassificationVocabulary | null;
  rowLayout: RowLayout;
  // The live role identity (Task 3's KnownRole|null). Optional/undefined ONLY for test doubles
  // written before role-threading existed — every real Provider mount (App.tsx) supplies it.
  // Governs the placeholder floor below: omitted here is read as "caller doesn't care about
  // role-gating" and keeps pre-existing K-default behaviour; explicit `null` is Task 3's real
  // "config never resolved" signal and gets the placeholder floor exactly like an unknown role.
  role?: KnownRole | null;
  // Mirrors App.tsx's own configSettled — false ONLY during the genuine first-ever /config fetch
  // of a session (no sessionStorage vocabulary cache to paint from yet). Omitted (every existing
  // test double written before this field existed) reads as settled/resolved, same convention as
  // `role` above — a caller that doesn't pass it is declaring "not testing the loading window",
  // never an implicit "still loading". Consumers read this via configLoading below — the
  // CONFIG-settle window, not a vocabulary-specific one — rather than reaching into this field
  // directly.
  configSettled?: boolean;
  // The served claim-kind -> Bearbeitung handling map (core, part 4c /config field). Optional/
  // absent reads as {} — same convention as role/configSettled above: a caller that doesn't pass it is
  // declaring "not testing this", never an implicit handling grant. Consumers: shared.tsx's row-3
  // decision badge and draftKindOrder's draft-slot ordering (services/draftClaims.ts).
  decisionKindHandling?: Readonly<Record<string, string>>;
  // The served flag landings (core generation 15; part 7 fix loop, 2026-10-12): flag id -> where it
  // lands the row and whether that landing implies it. Optional/absent reads as {} — nothing is
  // implied by omission, so every flag stays drawn. Consumer: services/impliedSignals.
  flagLandings?: Readonly<Record<string, FlagLanding>>;
  // The served form-extraction capability (/config `formExtractionEnabled` — the SAME App state that
  // gates the Excel-batch button; part 7 fix loop, 2026-10-12). Carried here so every list-row surface
  // and the row-pill registry read one gate for the „Formular" pill without prop-drilling it through
  // each page. Optional/absent reads as OFF — a capability is never granted by omission.
  formExtractionEnabled?: boolean;
  // Whether the AUTHORITATIVE /config fetch has answered, so the capability flags above mean what
  // they say (part 7 fix loop, 2026-10-12). Unlike configSettled it is never seeded from the
  // session cache — capability flags are never cached. `false` = still unknown; omitted (every test
  // double that is not testing this window) reads as settled, the configSettled convention.
  capabilitiesSettled?: boolean;
  // refresh = the app root's config refetch, exposed so a settings save can reach every mounted row
  // without a reload (part 4b eyeball finding 1).
  refresh?: () => void;
};
const SchemaCtx = createContext<Ctx>({ schema: null, vocabulary: null, rowLayout: 'strip_label' });
export const ClassificationSchemaProvider = SchemaCtx.Provider;

/** The whole `useCategoryMeta()` surface as a type — for a pure helper that takes the resolved meta
 *  as an argument rather than calling the hook itself (first consumer: services/searchFaceIds,
 *  part 7, 2026-10-11). */
export type CategoryMeta = ReturnType<typeof useCategoryMeta>;

export function useCategoryMeta() {
  const { schema, vocabulary, rowLayout, role, configSettled, decisionKindHandling: decisionKindHandlingCtx, flagLandings: flagLandingsCtx, formExtractionEnabled: formExtractionEnabledCtx, capabilitiesSettled, refresh: refreshCtx } = useContext(SchemaCtx);
  const formExtractionEnabled = formExtractionEnabledCtx === true;
  // `=== false`, not `!capabilitiesSettled`: an omitted flag reads as settled (see Ctx).
  const capabilitiesLoading = capabilitiesSettled === false;
  // Genuinely "still resolving the config fetch" — distinct from vocabularyMissing below, which is
  // a TERMINAL state (config resolved, this role simply owns no schema/vocabulary). This is the
  // CONFIG-settle window, not a vocabulary-specific one: during it every vocab-derived string
  // (categoryNoun, label(), …) would otherwise fall through to the raw `{category.…}` placeholder
  // floor because role/schema/vocab are ALL still their neutral pre-fetch defaults — a
  // vocab-dependent consumer must render a skeleton instead of reading any of those accessors while
  // this is true (UI Layout Stability Rule: shimmer, never placeholder text); any other
  // config-dependent consumer reads the same signal for its own settle window. `configSettled ===
  // false` (not `!configSettled`) so an omitted/undefined flag — every test double written before
  // Task C — reads as settled, per this file's own role convention.
  const configLoading = configSettled === false;
  // Precedence, and the ONLY place it is decided: vocabulary → legacy schema → K-defaults.
  // gMSB reaches the K-defaults because it has NO vocabulary, not because anything asks its name —
  // its legacy rendering is preserved structurally rather than by a branch someone must remember.
  const vocab = vocabulary ?? null;
  // role omitted (an un-threaded test double) is read IDENTICALLY to role === null (Task 3's real
  // "config never resolved" signal) — never as an implicit gMSB request. The K-defaults are
  // gMSB/wMSB's OWN vocabulary, not a generic no-schema fallback, so no other role, known or not,
  // may silently inherit them; the arc's own rule ("no silent gmsb assumption anywhere") does not
  // carve out an exception for tests. An empty categoryKeys here is not silent: the only way it
  // happens is customerRole === null, the exact condition under which App.tsx already renders the
  // global RoleConfigWarning overlay — the warning IS on screen when the list goes empty.
  // RECONCILIATION enumeration: every value the tenant's primary axis carries, unclassified bucket included. A
  // total computed over these reconciles against the ticket count — rejection-flow heatmaps,
  // force-category pickers, anything that would silently lose rows by omitting a value.
  const categoryKeys = useMemo(
    () => vocab ? vocab.primary.values.map(v => v.id)
        : schema ? schema.marktprozess.map(v => v.id)
        : ownsMsbKVocabulary(role) ? GMSB_STAT_KEYS
        : [],
    [vocab, schema, role],
  );
  // CATEGORIZATION-PERFORMANCE enumeration: the same list minus every value the tenant marked
  // `excludedFromEvaluation`. The two sets are NOT interchangeable — an unclassified category ("could not
  // be classified") is not a classification outcome, so counting it distorts every rate computed
  // over the set, while dropping it from a reconciliation total silently loses rows.
  //
  // The marker is SERVED, and that is the point: this file used to encode the same fact as
  // `CATEGORY_KEYS.filter(k => k !== UNCLASSIFIED_KEY)` — correct only because gMSB happens to
  // spell its unclassified value 'KU'. A role whose unclassified value is spelled anything else needs no frontend change now.
  // GMSB_STAT_KEYS survives as the LEGACY-FLOOR arm only (no vocabulary served yet); the moment a
  // vocabulary arrives, the marker answers instead and the literal is never consulted.
  //
  // The other two arms are deliberately identical to categoryKeys': a legacy ClassificationSchema
  // carries no marker (nothing to drop), and the floor list already excludes gMSB's unclassified value. So the
  // two sets diverge in exactly ONE state — a served vocabulary that marks something — which is
  // why delivering vocabulary rows to an MSB tenant before this split existed would have flipped
  // every performance surface to include KU.
  const performanceCategoryKeys = useMemo(
    () => vocab ? vocab.primary.values.filter(v => !v.excludedFromEvaluation).map(v => v.id)
        : schema ? schema.marktprozess.map(v => v.id)
        : ownsMsbKVocabulary(role) ? GMSB_STAT_KEYS
        : [],
    [vocab, schema, role],
  );
  // The distinguishing flag stats surfaces must read before treating an empty categoryKeys as
  // "zero tickets": a KNOWN role (BE has told us who this tenant is) that does NOT own the legacy
  // K-vocabulary, with neither schema nor vocabulary loaded, is a role-configuration gap — e.g. a
  // freshly-provisioned VNB tenant before its taxonomy is seeded — not a data-volume fact. This is
  // reachable WITHOUT the global RoleConfigWarning (customerRole resolved fine; it's the tenant's
  // vocabulary/schema payload that's empty), so a stats surface that renders a generic "Keine
  // Daten" here would be indistinguishable from a real empty dataset — the silent-empty state the
  // placeholder floor forbids. `!!role && isKnownRole(role)` is defensive belt-and-braces: the Ctx
  // type already only allows KnownRole|null, but a drifted/`as any` role must not evaluate true.
  const vocabularyMissing = !!role && isKnownRole(role) && !ownsMsbKVocabulary(role) && !schema && !vocab;
  // Second axis. Vocabulary: the secondary axis' ids. VNB: anliegen ids from schema. gMSB has no
  // schema-anliegen — its second-axis option source is the REASON_CODES constant.
  const secondaryAxisKeys = useMemo(
    () => vocab ? (vocab.secondary?.values.map(v => v.id) ?? [])
        : schema ? schema.anliegen.map(v => v.id)
        : [],
    [vocab, schema],
  );
  // Label lookup built from the tenant's own data. Falls through to the legacy resolver — which
  // still backstops K-codes, H-codes and process types via CATEGORY_LABELS — so a value missing
  // from the vocabulary degrades to a readable German label, never a raw id.
  const vocabLabels = useMemo(() => {
    if (!vocab) return null;
    const m = new Map<string, string>();
    for (const v of vocab.primary.values) m.set(v.id, v.label);
    for (const v of vocab.secondary?.values ?? []) m.set(v.id, v.label);
    return m;
  }, [vocab]);
  // The served claim-kind -> Bearbeitung handling map — {} when the provider passes none (an
  // un-threaded test double, or a role with no declared handling). NEVER undefined: see the
  // field's own comment on Ctx above. Consumers: shared.tsx's row-3 decision badge and
  // draftKindOrder's draft-slot ordering (services/draftClaims.ts).
  const decisionKindHandling = useMemo(() => decisionKindHandlingCtx ?? {}, [decisionKindHandlingCtx]);
  // The served flag landings — {} when the provider passes none (see the field on Ctx).
  const flagLandings = useMemo(() => flagLandingsCtx ?? {}, [flagLandingsCtx]);
  // The app root's config refetch, exposed through the same seam — a safe no-op when the provider
  // passes none (every un-threaded test double).
  const refresh = useCallback(() => { refreshCtx?.(); }, [refreshCtx]);
  return useMemo(() => {
    const label = (v: string) => vocabLabels?.get(v) ?? resolveLabel(schema, v, role);
    // The tenant's vocabulary is passed to `color` as of vocabulary v7 (2026-09-13): a value's hue
    // is a SERVED fact now, and this accessor was the one seam that still resolved it from the
    // baked maps alone — so a tenant re-hueing a value reached every other surface's word and none
    // of their colour.
    const color = (v: string) => resolveColor(schema, vocab, v);
    // The primary-axis paint (fill/ink/border) — the unclassified value the quiet slate tint, never coloured.
    const swatch = (v: string) => resolvePrimarySwatch(schema, vocab, v);
    const isUnclassified = (v: string) => isUnclassifiedValue(vocab, v);
    const abbr = (v: string) => resolveAbbr(vocab, v);
    // The rest of the served presentation trio. Both answer null rather than a guess (see their
    // resolvers): a renderer shows no glyph, and a tooltip shows the label alone.
    const icon = (v: string) => resolveIcon(vocab, v);
    const hint = (v: string) => resolveHint(vocab, v);
    // ── Classified-alongside ATTRIBUTES (vocabulary v7). Two accessors, because surfaces ask two
    // different questions: a filter section needs the DECLARATION (its German heading and its value
    // list — `attribute`), while a row pill needs ONE value's FACE (`attributeFace`, whose null
    // means render nothing — the served suppression, kept distinct from an undeclared value's
    // propercased stand-in inside the resolver).
    const attribute = (name: string) => resolveAttribute(vocab, name);
    const attributeFace = (name: string, value: string) => resolveAttributeFace(vocab, name, value);
    // The served WORD for a backend-emitted id (badge-faces round) — decision/registry_status/
    // bucket/outcome and whatever family a future surface reads. See resolveFace for the two-
    // absence floor.
    const face = (family: string, key: string) => resolveFace(vocab, family, key);
    // The served word ONLY (no raw-key floor) — see resolveServedWord: what a filter section gates on.
    const servedWord = (family: string, key: string) => resolveServedWord(vocab, family, key);
    // grund-code -> category-band mapping (D10 addendum 4), the seam counterpart of `label`. gMSB/
    // wMSB only today (`vocab.secondary` is the grund_code axis) — a schema-driven role has no
    // second-axis min/max concept, so `vocab` is the only source this reads; a legacy/schema-only
    // role falls straight through resolveCategoriesForSecondaryAxisCode's own floor.
    const categoriesForSecondaryAxisCode = (code: string) => resolveCategoriesForSecondaryAxisCode(vocab, code);
    // One flag's served landing, or null when the tenant serves none for it (part 7 fix loop,
    // 2026-10-12). Read through services/impliedSignals, never compared at a call site.
    const flagLanding = (flagId: string): FlagLanding | null => flagLandings[flagId] ?? null;
    // The hover carries the served wording in full, and the raw code when there is none.
    // Tier order MIRRORS `label`'s own (vocabulary → legacy schema → gMSB's baked catalog → the
    // raw code, never an invented word) — found missing its schema tier while wiring Task 4's
    // display surfaces onto this accessor: a VNB tenant on a pre-vocabulary backend (schema, no
    // vocab — a live, measured fleet state, not hypothetical) was falling straight from
    // vocabLabels (always empty there) to the gMSB reason-code catalog, floor-ing every anliegen
    // hover to its own raw id instead of the schema's resolved label. mp/an duplicate
    // resolveLabel's own schema lookup rather than calling it directly, because resolveLabel's
    // OWN floor (the `{category.…}` placeholder / CATEGORY_LABELS) is a different, gMSB/wMSB-
    // scoped floor than this accessor's (the raw code, unconditionally, never a borrowed word).
    const secondaryAxisHover = (code: string) =>
      vocabLabels?.get(code)
      ?? schema?.marktprozess.find(v => v.id === code)?.label
      ?? schema?.anliegen.find(v => v.id === code)?.label
      ?? getReasonCodeLabel(code);
    // THE secondary-axis pair, and the only place either question is answered.
    // WORDING (operator ruling 2026-09-08, walk round 4 + review round 1): the RESOLVED label —
    // the tenant's own word. It floors to the DE-SLUGGED form (never a raw id with underscores)
    // when nothing resolves the code, which is the honest reading of an unseeded value on a surface
    // that has no room to explain itself; the hover keeps the raw code, and that pairing is what
    // identifies the gap. CASING is NOT decided here: the axis2 register lowercases the pill face
    // (SecondaryAxisPills), while the two interactive chips that read this accessor and render
    // their own markup (FilterPanel's filter bubble, UploadsSeite's removable chip) show the word
    // as served. One seam for the WORD, which is what must never diverge.
    // ⚠ A vocabulary DELIVERY is visible on these surfaces: a tenant whose vocabulary lands changes
    // the word from the de-slugged `termin status` to the served `Reine Termin-/Statusanfrage`.
    // The operator ruled the wording, knowing the delivery shows.
    const secondaryAxisText = (code: string) => {
      const resolved = secondaryAxisHover(code);
      return resolved === code ? formatSecondaryAxisCode(code) : resolved;
    };
    return {
      // The two SOURCE flags. Read one of these only when the answer genuinely depends on WHICH
      // source arrived; for "does this tenant label its categories from its own data?" — which is
      // what almost every call site is actually asking — read categoryLabelsFromData below.
      schemaActive: !!schema,
      vocabularyActive: !!vocab,
      // THE CALL-SITE DISCRIMINATOR — "does this tenant label its categories from its own data?"
      // Neither source flag alone is correct, and the asymmetry is measured, not hypothetical (live
      // fleet, 2026-08-05): a wim payload carries a vocabulary and a NULL legacy schema, while a vnb
      // tenant on a backend predating the vocabulary field carries a schema and NO vocabulary. Both
      // label from tenant data; only gMSB/wMSB, carrying neither, fall through to the K-defaults.
      // Gating on vocabulary alone would break this seam's own promise that an older backend behaves
      // exactly as it did before the field existed.
      //
      // ⚠ A short-lived presentation lever (UI_CATEGORY_LABEL_MODE / categoryLabelMode) once
      // conjoined a SECOND question here ("does this tenant WANT labels") and defaulted gmsb/wmsb/
      // wim to 'abbr' — which forced this flag false even on a wim tenant that has always served a
      // full vocabulary, sending five call sites down the gMSB K-code fallback path on a tenant that
      // owns none of it (Archiv badge, Statistik heatmap, ReanalysisModal options, the secondary-axis
      // filter, Uploads category options). The lever is deleted; this flag answers ONE question again.
      categoryLabelsFromData: !!vocab || !!schema,
      rowLayout,
      // The role's REGISTRY DEFAULT row layout — i.e. what `rowLayout` would be if nobody had
      // touched it (walk fix round 2, 2026-09-09). Exposed as the DEFAULT rather than the raw role
      // so a surface can ask the question it actually has ("is this setting a deliberate flip?")
      // without spelling a role name or re-deriving the map. The Archiv row's badge face is the one
      // caller today: a strip_abbr-default role sitting on strip_label got there by a clerk's
      // choice, and the badge wears the short axis id there rather than repeating the served word
      // its resolution pill already carries.
      rowLayoutDefault: defaultRowLayout(role),
      label,
      color,
      swatch,
      isUnclassified,
      abbr,
      icon,
      hint,
      // The question a row's face actually asks: does an approval need its own word because it
      // can differ from the classification? Only when more than one kind STATES a Bearbeitung in
      // the served handling map (spec §14.4) — the count of approvable kinds is not what decides
      // it (`archive` may be approvable on a role whose axis is not a Bearbeitung).
      statesApprovedKind: Object.keys(decisionKindHandling).length > 1,
      decisionKindHandling,
      // The served form-extraction capability (see Ctx) — the gate of the row's „Formular" pill and
      // of its registry switch; never a role id.
      formExtractionEnabled,
      // True until the authoritative /config answer lands (see Ctx) — while it is, a capability-gated
      // surface reserves its footprint rather than reading formExtractionEnabled's provisional false.
      capabilitiesLoading,
      refresh,
      attribute,
      attributeFace,
      face,
      servedWord,
      categoriesForSecondaryAxisCode,
      flagLanding,
      secondaryAxisText,
      secondaryAxisHover,
      // ── The two enumerations. Pick by the QUESTION, not by convenience (see their derivations
      // above): does this surface reconcile against a total, or does it measure categorization
      // performance? Getting it wrong is silent in both directions — a lost row, or a flattered rate.
      //
      // RECONCILIATION — every served value, unclassified bucket included.
      // VNB: marktprozess ids from schema. gMSB with vocabulary: K0–K4 + KU. gMSB on the legacy
      // floor (no vocabulary yet): K0–K4, because the floor list predates the marker.
      categoryKeys,
      // CATEGORIZATION PERFORMANCE — unclassified values dropped via the served `excludedFromEvaluation`.
      performanceCategoryKeys,
      // true only when categoryKeys is [] FOR ROLE-CONFIGURATION REASONS (see above) — stats
      // surfaces read this to render the "Rollenkonfiguration unvollständig" notice instead of a
      // generic empty state.
      vocabularyMissing,
      // True only during the genuine first-fetch window (see above) — the CONFIG-settle signal,
      // not a vocabulary-specific one, though today's consumers are all vocab-derived chrome
      // (treemap, category-noun titles): read this BEFORE vocabularyMissing/categoryLabelsFromData/
      // any label accessor, since none of those has a trustworthy answer yet, and render a shimmer
      // skeleton in the pre-allocated space instead.
      configLoading,
      // VNB: anliegen ids from schema; gMSB: [] (use REASON_CODES instead)
      secondaryAxisKeys,
      // ── The axis nouns, in the three German forms the UI actually needs.
      //
      // Plural and compound EXIST BECAUSE THIS UI ASSEMBLES GERMAN BY CONCATENATION —
      // "{noun}verteilung", "Häufigste {noun}". That held only while every noun was convenient:
      // "Kategorie"/"Marktprozess" need no linking element and pass as plurals. "Bearbeitung" does
      // neither, so glued bare it yields "Bearbeitungverteilung" and "Häufigste Bearbeitung".
      //
      // ONLY the forms that have call sites are exposed, and each legacy fallback is the string
      // that site renders TODAY — not a form derived by rule. Exposing a form nobody uses would
      // mean inventing its legacy value, and the next author would find an untested string sitting
      // there looking authoritative. categoryNounPlural and secondaryAxisNounCompound are deliberately
      // ABSENT for that reason; add them with their site, never ahead of it.
      //
      // ⚠ TRIPWIRE, mirroring the payload's: a FOURTH form means concatenation is exhausted and
      // these sites must take COMPLETE PHRASES instead — not another accessor. German-scoped.

      // VNB: 'Marktprozess'; gMSB/wMSB: 'Kategorie'; other/unresolved: '{category.noun}'
      categoryNoun: vocab?.primary.noun ?? resolveCategoryNoun(schema, role),
      // For sites that GLUE a suffix on: "{…}verteilung", "Keine {…}daten" (the confidence pill says
      // the pipeline noun Klassifikationskonfidenz and glues nothing).
      // 'Kategorie'/'Marktprozess' glue correctly as-is, so their fallback is the base noun.
      categoryNounCompound: vocab?.primary.nounCompound ?? vocab?.primary.noun ?? resolveCategoryNoun(schema, role),
      // VNB: 'Anliegen'; gMSB/wMSB: 'Grund-Code'; other/unresolved: '{anliegen.noun}'
      secondaryAxisNoun: vocab?.secondary?.noun ?? resolveSecondaryAxisNoun(schema, role),
      // For counting sites: "Häufigste …", "Alle …", "Weitere …". ⚠ The legacy fallback is
      // resolveSecondaryAxisNounPlural, NOT the base noun — gMSB renders 'Grund-Codes' there today,
      // so falling back to 'Grund-Code' would silently regress the role this arc must not touch.
      secondaryAxisNounPlural: vocab?.secondary?.nounPlural ?? vocab?.secondary?.noun ?? resolveSecondaryAxisNounPlural(schema, role),
    };
  }, [schema, vocab, vocabLabels, rowLayout, categoryKeys, performanceCategoryKeys, vocabularyMissing, configLoading, secondaryAxisKeys, role, decisionKindHandling, flagLandings, formExtractionEnabled, capabilitiesLoading, refresh]);
}
